import React, { useState, useEffect } from 'react';
import { Shield, Search, DollarSign, Calendar, AlertCircle, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Link2Off, Link, X, Briefcase } from 'lucide-react';
import api from '../../services/api';

function StaffTrustAccounts() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [showUnmatchedOnly, setShowUnmatchedOnly] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [summary, setSummary] = useState({ total: 0, deposits: 0, disbursements: 0, unmatched: 0, unmatchedAmount: 0 });

  // Link to case modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [cases, setCases] = useState([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [txnRes, ledgerRes] = await Promise.all([
        api.get('/trust/transactions?limit=500'),
        api.get('/trust/ledgers')
      ]);

      const txns = Array.isArray(txnRes.data) ? txnRes.data : [];
      const ldgrs = Array.isArray(ledgerRes.data) ? ledgerRes.data : [];

      setTransactions(txns);
      setLedgers(ldgrs);

      // Identify unmatched ledgers (no case_id or "General" in name)
      const unmatchedLedgerIds = new Set(
        ldgrs
          .filter(l => !l.case_id || l.ledger_name?.includes('General'))
          .map(l => l.id)
      );

      // Calculate summary
      const activeTxns = txns.filter(t => !t.is_voided);
      const deposits = activeTxns.filter(t => t.transaction_type === 'DEPOSIT')
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      const disbursements = activeTxns.filter(t => t.transaction_type === 'DISBURSEMENT')
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      const unmatchedTxns = activeTxns.filter(t => unmatchedLedgerIds.has(t.client_ledger_id));
      const unmatchedAmount = unmatchedTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);

      setSummary({
        total: activeTxns.length,
        deposits,
        disbursements,
        unmatched: unmatchedTxns.length,
        unmatchedAmount
      });
    } catch (err) {
      console.error('Error fetching trust data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async (search = '') => {
    try {
      const response = await api.get(`/cases/?search=${encodeURIComponent(search)}&limit=50`);
      setCases(Array.isArray(response.data) ? response.data : response.data?.cases || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  };

  const openLinkModal = (txn) => {
    setSelectedTransaction(txn);
    setShowLinkModal(true);
    setCaseSearch('');
    setLinkError('');
    fetchCases('');
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setSelectedTransaction(null);
    setCaseSearch('');
    setLinkError('');
  };

  const handleCaseSearch = (value) => {
    setCaseSearch(value);
    fetchCases(value);
  };

  const linkToCase = async (caseId) => {
    if (!selectedTransaction) return;

    try {
      setLinking(true);
      setLinkError('');
      await api.post(`/trust/transactions/${selectedTransaction.id}/reassign`, {
        case_id: caseId
      });
      closeLinkModal();
      fetchData(); // Refresh the list
    } catch (err) {
      console.error('Error linking transaction:', err);
      setLinkError(err.response?.data?.detail || 'Failed to link transaction to case');
    } finally {
      setLinking(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getLedger = (ledgerId) => {
    return ledgers.find(l => l.id === ledgerId);
  };

  const getLedgerName = (ledgerId) => {
    const ledger = getLedger(ledgerId);
    return ledger?.client_name || ledger?.ledger_name || 'Unknown';
  };

  const isUnmatchedLedger = (ledgerId) => {
    const ledger = getLedger(ledgerId);
    return !ledger?.case_id || ledger?.ledger_name?.includes('General');
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      DEPOSIT: 'bg-green-500/20 text-green-400 border-green-500/30',
      DISBURSEMENT: 'bg-red-500/20 text-red-400 border-red-500/30',
      TRANSFER: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      FEE_TRANSFER: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      REFUND: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const filteredTransactions = transactions.filter(txn => {
    if (txn.is_voided) return false;

    const ledgerName = getLedgerName(txn.client_ledger_id).toLowerCase();
    const matchesSearch =
      (txn.description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (txn.payor_payee?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      ledgerName.includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'ALL' || txn.transaction_type === filterType;
    const matchesUnmatched = !showUnmatchedOnly || isUnmatchedLedger(txn.client_ledger_id);

    return matchesSearch && matchesType && matchesUnmatched;
  });

  if (loading) {
    return (
      <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="animate-spin text-virgo-teal-400 mr-2" size={20} />
          <span className="text-virgo-teal-200">Loading trust accounts...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h2 className="text-xl font-semibold text-virgo-teal-100 flex items-center">
          <Shield className="mr-2 text-cyan-400" size={20} />
          Trust Account Transactions
        </h2>
        <button className="text-virgo-teal-400 hover:text-virgo-teal-200">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {expanded && (
        <>
          {/* Info Banner */}
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="text-amber-400 mr-3 mt-0.5 flex-shrink-0" size={18} />
              <div>
                <p className="text-amber-300 text-sm font-medium">Clio Import Review</p>
                <p className="text-amber-400/80 text-xs mt-1">
                  Review transactions imported from Clio. Click "Link" on unmatched transactions to assign them to a case.
                </p>
              </div>
            </div>
          </div>

          {/* Unmatched Warning */}
          {summary.unmatched > 0 && (
            <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <AlertTriangle className="text-red-400 mr-3 mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-red-300 font-medium">
                      {summary.unmatched} Unmatched Transactions ({formatCurrency(summary.unmatchedAmount)})
                    </p>
                    <p className="text-red-400/80 text-sm mt-1">
                      These transactions are not linked to any client or matter in Virgo.
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowUnmatchedOnly(!showUnmatchedOnly); }}
                  className={`px-3 py-1.5 text-sm rounded-lg transition flex items-center ${
                    showUnmatchedOnly
                      ? 'bg-red-500 text-white'
                      : 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                  }`}
                >
                  <Link2Off size={14} className="mr-1.5" />
                  {showUnmatchedOnly ? 'Show All' : 'Show Unmatched'}
                </button>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-navy-600">
              <p className="text-xs text-virgo-teal-400 uppercase tracking-wide">Total Transactions</p>
              <p className="text-2xl font-bold text-virgo-teal-100 mt-1">{summary.total}</p>
            </div>
            <div className="bg-virgo-navy-700 rounded-lg p-4 border border-green-500/20">
              <p className="text-xs text-green-400 uppercase tracking-wide">Total Deposits</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(summary.deposits)}</p>
            </div>
            <div className="bg-virgo-navy-700 rounded-lg p-4 border border-red-500/20">
              <p className="text-xs text-red-400 uppercase tracking-wide">Total Disbursements</p>
              <p className="text-2xl font-bold text-red-400 mt-1">{formatCurrency(summary.disbursements)}</p>
            </div>
            <div className={`bg-virgo-navy-700 rounded-lg p-4 border ${summary.unmatched > 0 ? 'border-amber-500/30' : 'border-virgo-navy-600'}`}>
              <p className="text-xs text-amber-400 uppercase tracking-wide">Unmatched</p>
              <p className={`text-2xl font-bold mt-1 ${summary.unmatched > 0 ? 'text-amber-400' : 'text-virgo-teal-100'}`}>
                {summary.unmatched}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500" size={18} />
              <input
                type="text"
                placeholder="Search by description, payor/payee, or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100 placeholder-virgo-teal-600 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="ALL">All Types</option>
              <option value="DEPOSIT">Deposits</option>
              <option value="DISBURSEMENT">Disbursements</option>
              <option value="TRANSFER">Transfers</option>
              <option value="FEE_TRANSFER">Fee Transfers</option>
            </select>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-600 transition flex items-center"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </button>
          </div>

          {/* Transactions Table */}
          <div className="mt-4 overflow-hidden rounded-lg border border-virgo-navy-600">
            {filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full">
                  <thead className="bg-virgo-navy-700 sticky top-0">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">Client/Matter</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">Description</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">Amount</th>
                      <th className="text-center py-3 px-4 text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-virgo-navy-700">
                    {filteredTransactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-virgo-navy-700/50 transition">
                        <td className="py-3 px-4">
                          <div className="flex items-center text-sm text-virgo-teal-200">
                            <Calendar size={14} className="mr-2 text-virgo-teal-500" />
                            {formatDate(txn.transaction_date)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full border ${getTransactionTypeColor(txn.transaction_type)}`}>
                            {txn.transaction_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center">
                            {isUnmatchedLedger(txn.client_ledger_id) && (
                              <Link2Off size={14} className="text-amber-400 mr-2 flex-shrink-0" title="Not linked to a client/matter" />
                            )}
                            <span className={isUnmatchedLedger(txn.client_ledger_id) ? 'text-amber-300' : 'text-virgo-teal-100'}>
                              {getLedgerName(txn.client_ledger_id)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-virgo-teal-300 max-w-xs truncate" title={txn.description}>
                          {txn.description || '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium ${
                            txn.amount >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {isUnmatchedLedger(txn.client_ledger_id) ? (
                            <button
                              onClick={() => openLinkModal(txn)}
                              className="px-3 py-1 text-xs bg-virgo-teal-600 text-white rounded hover:bg-virgo-teal-500 transition flex items-center mx-auto"
                            >
                              <Link size={12} className="mr-1" />
                              Link
                            </button>
                          ) : (
                            <span className="text-xs text-virgo-teal-600">Linked</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <DollarSign className="mx-auto text-virgo-teal-600 mb-4" size={48} />
                <h3 className="text-lg font-medium text-virgo-teal-200 mb-2">No Transactions Found</h3>
                <p className="text-virgo-teal-400 text-sm">
                  {searchTerm || filterType !== 'ALL'
                    ? 'No transactions match your filters.'
                    : 'No trust transactions have been recorded yet.'}
                </p>
              </div>
            )}
          </div>

          {/* Results Count */}
          {filteredTransactions.length > 0 && (
            <div className="mt-3 text-xs text-virgo-teal-500">
              Showing {filteredTransactions.length} of {summary.total} transactions
            </div>
          )}
        </>
      )}

      {/* Link to Case Modal */}
      {showLinkModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={closeLinkModal}>
          <div className="bg-virgo-navy-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-virgo-navy-600 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-virgo-teal-100 flex items-center">
                <Link size={20} className="mr-2 text-virgo-teal-400" />
                Link Transaction to Case
              </h3>
              <button onClick={closeLinkModal} className="text-virgo-teal-500 hover:text-virgo-teal-300">
                <X size={20} />
              </button>
            </div>

            {/* Transaction Info */}
            <div className="bg-virgo-navy-700 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-virgo-teal-500">Date:</span>
                  <span className="text-virgo-teal-200 ml-2">{formatDate(selectedTransaction.transaction_date)}</span>
                </div>
                <div>
                  <span className="text-virgo-teal-500">Amount:</span>
                  <span className={`ml-2 font-medium ${selectedTransaction.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(selectedTransaction.amount)}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-virgo-teal-500">Description:</span>
                  <span className="text-virgo-teal-200 ml-2">{selectedTransaction.description || '-'}</span>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {linkError && (
              <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {linkError}
              </div>
            )}

            {/* Case Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500" size={18} />
              <input
                type="text"
                placeholder="Search for a case by client name or case number..."
                value={caseSearch}
                onChange={(e) => handleCaseSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100 placeholder-virgo-teal-600 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                autoFocus
              />
            </div>

            {/* Case List */}
            <div className="max-h-64 overflow-y-auto border border-virgo-navy-600 rounded-lg">
              {cases.length > 0 ? (
                <div className="divide-y divide-virgo-navy-700">
                  {cases.map(c => (
                    <button
                      key={c.id}
                      onClick={() => linkToCase(c.id)}
                      disabled={linking}
                      className="w-full text-left px-4 py-3 hover:bg-virgo-navy-700 transition flex items-center justify-between disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <Briefcase size={16} className="text-virgo-teal-500 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-virgo-teal-100">{c.client_name}</div>
                          <div className="text-xs text-virgo-teal-500">{c.display_number || c.case_number}</div>
                        </div>
                      </div>
                      <span className="text-xs text-virgo-teal-600">{c.status}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-virgo-teal-500 text-sm">
                  {caseSearch ? 'No cases found matching your search.' : 'Type to search for cases...'}
                </div>
              )}
            </div>

            {linking && (
              <div className="mt-4 flex items-center justify-center text-virgo-teal-400">
                <RefreshCw className="animate-spin mr-2" size={16} />
                Linking transaction...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffTrustAccounts;
